/**
 * Dev-only local auth. Mints a fake Cognito ID token in the browser so the console
 * can talk to a LOCAL backend without a real Cognito pool. The backend runs in
 * trust-gateway mode (AUTH_TRUST_GATEWAY=true, the default), which validates the
 * token's CLAIMS (issuer/expiry/use/audience/groups) but NOT its signature, so an
 * unsigned token with the right claims is accepted.
 *
 * STRICTLY gated to `vite dev`: `import.meta.env.DEV` is false in production builds,
 * so every export here is dead code (tree-shaken) in the deployed bundle. Never
 * relies on a secret — the backend it targets is a throwaway local instance.
 */
import type { AuthUser, Role } from './types';

export const DEV_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === 'true';

const STORAGE_KEY = 'habilita.devAuthToken';

const POOL_ID = (import.meta.env.VITE_COGNITO_USER_POOL_ID as string) || 'us-east-1_localdev';
const CLIENT_ID = (import.meta.env.VITE_COGNITO_CLIENT_ID as string) || 'localdevclient';
// Cognito issuers embed the region as the pool-id prefix (e.g. "us-east-1_xxxx").
const REGION = POOL_ID.split('_')[0] || 'us-east-1';
const ISSUER = `https://cognito-idp.${REGION}.amazonaws.com/${POOL_ID}`;

function b64url(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodePayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Simulate Cognito's forced first password change, so the invite's first-access
 * step can be driven locally and by the E2E suite. Same convention as the role
 * above: the e-mail's local-part decides.
 */
export function needsFirstPassword(email: string): boolean {
  return (email.split('@')[0]?.toLowerCase() ?? '').includes('primeiro-acesso');
}

/** Pick a role from the email local-part so RBAC can be exercised locally. */
export function roleForEmail(email: string): Role {
  const local = email.split('@')[0]?.toLowerCase() ?? '';
  if (local.includes('member')) return 'member';
  if (local.includes('editor')) return 'editor';
  return 'admin';
}

/** Build an unsigned Cognito-shaped ID token whose claims the backend accepts. */
export function mintDevToken(email: string): string {
  const now = Math.floor(Date.now() / 1000);
  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    iss: ISSUER,
    aud: CLIENT_ID,
    sub: `dev-${email}`,
    token_use: 'id',
    email,
    name: email.split('@')[0],
    'cognito:username': email,
    'cognito:groups': [roleForEmail(email)],
    iat: now,
    exp: now + 12 * 3600,
  };
  return `${b64url(JSON.stringify(header))}.${b64url(JSON.stringify(payload))}.devsig`;
}

export function devUserFromToken(token: string): AuthUser | null {
  const claims = decodePayload(token);
  if (!claims) return null;
  const email = String(claims.email ?? '');
  return {
    id: String(claims.sub ?? `dev-${email}`),
    email,
    displayName: email.split('@')[0] || email,
    role: roleForEmail(email),
  };
}

export function storeDevToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* storage unavailable — the session just won't persist across reloads */
  }
}

/** Returns the stored token, dropping it if expired so reloads land on login. */
export function loadDevToken(): string | null {
  try {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) return null;
    const claims = decodePayload(token);
    const exp = claims?.exp;
    if (typeof exp === 'number' && exp * 1000 < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function clearDevToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
